/*
 * Commercial Bundle Builder - section script.
 * Hosts vendor bundle cards (product-card-bundle / product-bundle-form) inside
 * category groups, keeps a per-category selection, previews savings, and adds
 * machine + add-ons to the cart in one request. Enforcement adapters:
 *   - none / automatic : nothing after add-to-cart
 *   - codes            : claim codes from the Workspace proxy and apply them via
 *                        /cart/update.js (the cross-page half lives in
 *                        assets/commercial-bundle-cart.js)
 * See docs/COMMERCIAL_BUNDLE_BUILDER_PLAN.md §3.
 */
(() => {
  if (customElements.get('commercial-bundle-builder')) return;

  const CLAIMS_KEY = 'mcs_bundle_claims';

  const readClaims = () => {
    try { return JSON.parse(window.localStorage.getItem(CLAIMS_KEY) || '{}') || {}; } catch (e) { return {}; }
  };
  const writeClaims = (claims) => {
    try { window.localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims)); } catch (e) { /* private mode */ }
  };
  const rememberClaim = (claim) => {
    if (window.mcsBundleCart && typeof window.mcsBundleCart.rememberClaim === 'function') {
      window.mcsBundleCart.rememberClaim(claim);
      return;
    }
    const claims = readClaims();
    claims[claim.bundleId] = claim;
    writeClaims(claims);
  };

  // At most one custom variant dropdown is open page-wide; holds its close().
  let openVariantDropdown = null;

  const normaliseToken = (token) => (typeof token === 'string' ? token.split('?')[0] : '');
  const newBundleId = () => `cb_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const money = (cents) => theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
  const fill = (template, values) => Object.keys(values).reduce((s, k) => s.split(`[${k}]`).join(values[k]), template);

  /* ---------- bench scene calibration ---------- */

  // Calibration for assets/bundle-builder-bg.jpg (natural 1672x941). The scene
  // layer replicates the wrapper's background geometry, so these are natural-
  // image pixels and sprite styles are plain percentages of the layer.
  // pxPerMm is the island's FRONT plane; relative product scale comes from the
  // per-product mm data, so this one constant is the only aesthetic tunable -
  // tune it with ?bench_debug=1 (docs/BUNDLE_BENCH_SCENE_PLAN.md §2, fact B7).
  const BENCH = {
    imgW: 1672,
    imgH: 941,
    pxPerMm: 0.36,
    arris: (x) => 508 + 0.013 * (x - 100), // bench-top front edge, img px
    spanMin: 140, // usable bench x-range (sink/tap/vase + panel zone beyond)
    spanMax: 990,
    machineX: 590, // machine anchor (centre), img px
    gapMm: 50, // real-world gap between placed items
    balanceMm: 260 // sides may differ by this much bench length before an item crosses over
  };

  // Height fallbacks (mm) by rule handle, for products whose
  // custom.bench_height_mm metafield is not seeded yet (CB-D3).
  const BENCH_FALLBACK_MM = {
    grinders: 400,
    'barista-tools': 120,
    cleaning: 200,
    coffee: 250,
    default: 250
  };

  const BENCH_BBOX_STORE = 'mcs_bench_bbox_v4'; // v4: reviewed alpha assets; discard runtime-mask cache
  const benchBBoxCache = new Map();

  const readStoredBBoxes = () => {
    try { return JSON.parse(window.sessionStorage.getItem(BENCH_BBOX_STORE) || '{}') || {}; } catch (e) { return {}; }
  };
  const storeBBox = (key, bbox) => {
    try {
      const all = readStoredBBoxes();
      all[key] = bbox;
      window.sessionStorage.setItem(BENCH_BBOX_STORE, JSON.stringify(all));
    } catch (e) { /* private mode / quota */ }
  };

  // Content bounding box of an image: alpha extents for cut-outs, else the
  // non-white extents (opaque photos, which the scene renders blended). Product
  // images carry heavy, inconsistent padding, so real-world scaling is wrong
  // without this trim. Resolves normalised {x0,y0,x1,y1} of the canvas, the
  // content aspect (w/h in px) and an `opaque` flag; never rejects - the
  // fallback is the untrimmed canvas.
  const measureBenchBBox = (src) => {
    if (benchBBoxCache.has(src)) return benchBBoxCache.get(src);
    const promise = new Promise((resolve) => {
      const stored = readStoredBBoxes()[src];
      if (stored) { resolve(stored); return; }
      const image = new Image();
      image.crossOrigin = 'anonymous'; // Shopify CDN sends ACAO:* (fact B4)
      const fallback = () => ({
        x0: 0, y0: 0, x1: 1, y1: 1,
        aspect: image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1,
        opaque: false
      });
      image.onerror = () => {
        // Transient load failure (the dev proxy drops requests occasionally):
        // keep the machine fallback / count missing add-on art for this render.
        // Drop the cache entry so a later interaction can retry.
        benchBBoxCache.delete(src);
        resolve({ ...fallback(), failed: true });
      };
      image.onload = () => {
        try {
          const scale = Math.min(1, 256 / Math.max(image.naturalWidth, image.naturalHeight));
          const w = Math.max(1, Math.round(image.naturalWidth * scale));
          const h = Math.max(1, Math.round(image.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(image, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          const scan = (isContent) => {
            let minX = w, minY = h, maxX = -1, maxY = -1;
            for (let y = 0; y < h; y += 1) {
              for (let x = 0; x < w; x += 1) {
                const i = (y * w + x) * 4;
                if (!isContent(data[i], data[i + 1], data[i + 2], data[i + 3])) continue;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
            return maxX < 0 ? null : { minX, minY, maxX, maxY };
          };
          let transparent = false;
          for (let i = 3; i < data.length; i += 4) { if (data[i] < 250) { transparent = true; break; } }
          // α ≥ 16 ignores soft-shadow halos; ≥246 on every channel reads as white bg.
          const isContent = transparent
            ? (r, g, b, a) => a >= 16
            : (r, g, b) => !(r >= 246 && g >= 246 && b >= 246);
          const box = scan(isContent);
          if (!box) { resolve(fallback()); return; }
          // Support baseline: the lowest row whose content run is wide (≥35% of
          // the bbox) - feet/drip tray, not a dangling portafilter handle or
          // spout. Every Expobar/Crem packshot has ~10% of its bbox below the
          // feet; without this the machine floats and renders ~10% small.
          const runWidth = (y) => {
            let min = -1, max = -1;
            for (let x = box.minX; x <= box.maxX; x += 1) {
              const i = (y * w + x) * 4;
              if (!isContent(data[i], data[i + 1], data[i + 2], data[i + 3])) continue;
              if (min < 0) min = x;
              max = x;
            }
            return max < 0 ? 0 : max - min + 1;
          };
          let baseY = box.maxY;
          const need = 0.35 * (box.maxX - box.minX + 1);
          for (let y = box.maxY; y >= box.minY; y -= 1) {
            if (runWidth(y) >= need) { baseY = y; break; }
          }
          const bbox = {
            x0: box.minX / w,
            y0: box.minY / h,
            x1: (box.maxX + 1) / w,
            y1: (box.maxY + 1) / h,
            base: (baseY + 1) / h,
            aspect: (box.maxX - box.minX + 1) / (box.maxY - box.minY + 1),
            opaque: !transparent
          };
          storeBBox(src, bbox);
          resolve(bbox);
        } catch (e) {
          resolve(fallback()); // tainted canvas etc. - render untrimmed rather than not at all
        }
      };
      image.src = src;
    });
    benchBBoxCache.set(src, promise);
    return promise;
  };

  class CommercialBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onInput = theme.utils.debounce(this.render.bind(this), 200);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPickedClick = this.onPickedClick.bind(this);
      this.hiddenBenchProducts = new Set();
      this.visibleBenchKeys = new Set();
    }

    connectedCallback() {
      const configEl = this.querySelector('[data-bundle-builder-config]');
      if (!configEl) return;
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.listObservers = [];
      this.config = JSON.parse(configEl.textContent);
      this.machine = { ...this.config.machine };
      this.strings = this.config.strings || {};
      this.enforcement = this.getAttribute('data-enforcement') || this.config.enforcement || 'none';
      this.showSavings = this.hasAttribute('data-show-savings');

      this.wrapper = this;
      this.submitButton = this.querySelector('[data-bundle-builder-submit]');
      this.statusEl = this.querySelector('[data-bundle-builder-status]');
      this.errorEl = this.querySelector('.product-form__error-message');
      this.pickedWrap = this.querySelector('[data-bundle-builder-picked]');
      this.pickedList = this.querySelector('[data-bundle-builder-picked-list]');
      this.pickedScroll = this.querySelector('[data-bundle-builder-picked-scroll]');
      this.pickedMoreUp = this.querySelector('[data-bundle-builder-picked-more="up"]');
      if (this.pickedList) {
        this.listen(this.pickedList, 'scroll', () => this.syncPickedScrollState(), { passive: true });
        if (this.pickedMoreUp) this.listen(this.pickedMoreUp, 'click', () => this.scrollPickedRows(-1));
      }

      this.listen(this, 'change', this.onChange);
      this.listen(this, 'input', this.onInput);
      this.listen(this.submitButton, 'click', this.onSubmit);
      this.listen(this.pickedList, 'click', this.onPickedClick);
      this.listen(this.querySelector('[data-bundle-builder-clear-all]'), 'click', () => this.clearAll());

      // The main product's <product-info> publishes variantChange with its runtime
      // section id (template--<id>__main-product on JSON templates) - read it from
      // the DOM rather than trusting the Liquid-side guess.
      const mainInfo = this.config.syncPageProduct !== false
        ? document.querySelector(`product-info[data-product-id="${this.machine.productId}"]`)
        : null;
      this.mainSectionId = mainInfo
        ? (mainInfo.getAttribute('data-original-section-id') || mainInfo.getAttribute('data-section-id'))
        : this.config.mainSectionId;

      this.variantUnsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (this.config.syncPageProduct === false) return;
        const data = event && event.data;
        if (!data || !data.variant) return;
        if (String(data.productId) !== String(this.machine.productId)) return;
        if (String(data.sectionId) === String(this.getAttribute('data-section-id'))) return;
        if (this.mainSectionId && String(data.sectionId) !== String(this.mainSectionId)) return;
        this.setMachineVariant(data.variant, data.variant.featured_media?.preview_image?.src);
      });

      this.wireTabs();
      this.observeStickyBar();

      // Theme editor: selecting a category or product discount reveals its panel.
      this.onBlockSelect = (event) => {
        const cat = event.target?.closest?.('[data-bundle-builder-category]');
        if (cat && this.contains(cat)) this.activateTab(cat.dataset.bundleBuilderCategory, { scroll: false });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Browsers may restore select/checkbox state on soft reload - re-sync the row data.
      this.querySelectorAll('[data-bundle-builder-item]').forEach((item) => this.syncVariant(item));
      this.querySelectorAll('[data-bundle-builder-check]').forEach((box) => {
        const wrap = box.closest('[data-bundle-builder-item]')?.querySelector('[data-bundle-builder-qty-wrap]');
        if (wrap) wrap.hidden = !box.checked;
      });

      this.enhanceVariantDropdowns();
      this.wireSearch();
      this.wireListFades();
      this.initMobileLayout();
      this.initBenchScene();
      const machineSelect = this.querySelector('[data-bundle-builder-machine-select]');
      if (machineSelect) this.onChange({ target: machineSelect });
      this.render();
      this.loadCoffeePrices();

      // The cart's "Finish your setup" link lands here (#build-your-setup) with the
      // machine already in the cart. onSubmit skips the machine line then; the totals
      // follow the cart so they don't charge for it again.
      this.cartUnsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => this.syncMachineInCart());
      this.syncMachineInCart();
      this.listen(document, 'click', this.onSetupLinkClick.bind(this));
      this.listen(window, 'hashchange', () => this.openFromHash());
      this.openFromHash();
    }

    disconnectedCallback() {
      this.cancelSavingsAnimation();
      this.displayedSavings = null;
      this.savingsTarget = null;
      this.listeners?.abort();
      this.listObservers?.forEach((observer) => observer.disconnect());
      this.listObservers = [];
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.variantUnsubscribe?.();
      this.cartUnsubscribe?.();
      this.releaseSetupHold?.();
      this.stickyObserver?.disconnect();
      this.variantDropdowns?.forEach(({ destroy }) => destroy());
      this.variantDropdowns = [];
      this.mobileObserver?.disconnect();
      this.modalObserver?.disconnect();
      cancelAnimationFrame(this.mobileFrame);
      this.mobileFooter?.classList.remove('is-fixed');
      this.syncMobileDock();
      if (this.mobileMql && this.onMobileChange) this.mobileMql.removeEventListener('change', this.onMobileChange);
      if (this.hasAttribute('data-hide-sticky-bar')) {
        document.querySelector('product-sticky-form')?.classList.remove('bundle-builder-suppressed');
      }
      // The wrapper outlives section re-renders in the theme editor; the scene
      // div is replaced with the section HTML but the class must not linger.
      if (this.sceneEl) this.clearBench();
      this.sceneEl = null;
    }

    /* ---------- mobile layout ---------- */

    get isMobile() {
      return !!this.mobileMql?.matches;
    }

    initMobileLayout() {
      this.mobileMql = window.matchMedia('(max-width: 1023px)');
      this.syncSectionTransitions();
      this.mobileFooter = this.querySelector('[data-bundle-builder-footer]');
      this.footerSlot = this.querySelector('[data-bundle-builder-footer-slot]');
      this.summaryToggle = this.querySelector('[data-bundle-builder-summary-toggle]');
      this.listen(this.summaryToggle, 'click', () => {
        this.summaryToggle.setAttribute('aria-expanded', this.summaryToggle.getAttribute('aria-expanded') !== 'true');
      });
      this.listen(this.querySelector('[data-bundle-builder-scene-overflow]'), 'click', () => {
        this.summaryToggle?.setAttribute('aria-expanded', 'true');
        const summary = this.querySelector('[data-bundle-builder-summary]');
        summary?.scrollIntoView({ block: 'center', behavior: this.scrollBehavior });
        (this.isMobile ? this.summaryToggle : this.pickedList?.querySelector('[data-bundle-builder-unpick]'))?.focus({ preventScroll: true });
      });
      this.onMobileChange = () => {
        this.syncSectionTransitions();
        this.positionBenchScene();
        this.renderBench(this.lastPicked || []);
        this.syncPickedScrollState();
        this.scheduleMobileLayout();
      };
      this.mobileMql.addEventListener('change', this.onMobileChange);
      const schedule = () => this.scheduleMobileLayout();
      this.listen(window, 'scroll', schedule, { passive: true });
      this.listen(window, 'resize', schedule, { passive: true });
      this.listen(window.visualViewport, 'resize', schedule, { passive: true });
      this.listen(window.visualViewport, 'scroll', schedule, { passive: true });
      this.listen(document, 'focusin', schedule);
      this.listen(document, 'focusout', schedule);
      this.modalObserver = new MutationObserver(schedule);
      this.modalObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      if ('ResizeObserver' in window) {
        this.mobileObserver = new ResizeObserver(schedule);
        this.mobileObserver.observe(this);
        if (this.mobileFooter) this.mobileObserver.observe(this.mobileFooter);
      }
      schedule();
    }

    syncSectionTransitions() {
      const section = this.closest('.commercial-bundle-builder-section');
      if (!section) return;
      for (const [edge, neighbour] of [['before', section.previousElementSibling], ['after', section.nextElementSibling]]) {
        if (!neighbour) continue;
        const surface = neighbour.querySelector('.section') || neighbour;
        const background = window.getComputedStyle(surface).getPropertyValue('--color-background').trim();
        if (background) section.style.setProperty(`--bundle-${edge}-background`, background);
      }
    }

    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    scheduleMobileLayout() {
      cancelAnimationFrame(this.mobileFrame);
      this.mobileFrame = requestAnimationFrame(() => this.syncMobileLayout());
    }

    syncMobileDock() {
      document.body.classList.toggle('bundle-mobile-purchase-active',
        !!document.querySelector('[data-bundle-builder-footer].is-fixed'));
    }

    syncMobileLayout() {
      if (!this.isConnected || !this.mobileFooter || !this.footerSlot) return;
      const rect = this.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height || window.innerHeight;
      const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select, [contenteditable="true"]');
      const modalOpen = document.body.matches('.has-modal-open, .has-drawer-open, .has-modal-opening, .has-cookie-banner');
      // Keep the one real footer/button in the builder: all pricing, busy and
      // error states remain shared, without reserving empty space in the panel.
      // Remember its height while hidden to keep the exit threshold stable.
      const footerHeight = this.mobileFooter.offsetHeight || this.mobileFooterHeight || 180;
      const fixed = this.isMobile && this.pickedCount > 0 && !editing && !modalOpen && (viewport?.scale || 1) <= 1
        && rect.top < viewportHeight - 180 && rect.bottom > footerHeight
        && Array.from(document.querySelectorAll('commercial-bundle-builder')).find((builder) => {
          const bounds = builder.getBoundingClientRect();
          return builder.pickedCount > 0 && bounds.top < viewportHeight - 180 && bounds.bottom > (builder.mobileFooter?.offsetHeight || builder.mobileFooterHeight || 180);
        }) === this;
      this.mobileFooter.classList.toggle('is-fixed', fixed);
      if (fixed) this.mobileFooterHeight = this.mobileFooter.offsetHeight || footerHeight;
      this.syncMobileDock();
    }

    // Section reloads and DOM moves must not leave duplicate handlers behind.
    listen(element, type, handler, options = {}) {
      element?.addEventListener(type, handler, { ...options, signal: this.listeners.signal });
    }

    setMachineVariant(variant, image) {
      this.machine.variantId = variant.id;
      this.machine.price = variant.price;
      this.machine.available = variant.available !== false;
      const artwork = this.config.machineArtwork?.[String(variant.id)];
      this.config.machine.benchHeightMm = artwork?.heightMm || this.config.machine.defaultBenchHeightMm || this.config.machine.benchHeightMm;
      this.machine.benchHeightMm = this.config.machine.benchHeightMm;
      const label = this.querySelector('[data-bundle-builder-machine-variant]');
      if (label) label.textContent = variant.title === 'Default Title' ? '' : (variant.title || '');
      const img = this.querySelector('.bundle-builder__preview-img');
      const source = artwork?.image || image || this.config.machineDefaultImage;
      if (img && source) { img.src = this.resize(source, 1200); img.removeAttribute('srcset'); }
      this.status('');
      this.render();
    }

    /* ---------- lookups ---------- */

    get categories() {
      return Array.from(this.querySelectorAll('[data-bundle-builder-category]'));
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    get tabs() {
      return Array.from(this.querySelectorAll('[data-bundle-builder-tab]'));
    }

    ruleFor(category) {
      const d = category.dataset;
      return {
        id: d.bundleBuilderCategory,
        handle: d.rule || '',
        claimRule: d.claimRule || d.rule || '',
        page: this.config.page || null,
        label: d.label || '',
        min: parseInt(d.min || '0', 10) || 0,
        allowQuantity: category.hasAttribute('data-allow-quantity')
      };
    }

    discountForItem(item) {
      if (item.closest('[data-bundle-builder-category]')?.dataset.rule === 'coffee') {
        const quote = this.coffeePrices?.[item.dataset.variantId];
        return { type: 'fixed_each', coffee: true, value: quote ? (quote.price_cents - quote.bundle_price_cents) / 100 : 0 };
      }
      const type = item.dataset.discountType || 'none';
      const value = Math.max(0, parseFloat(item.dataset.discountValue || '0') || 0);
      return { type, value: type === 'percentage' ? Math.min(100, value) : value };
    }

    async loadCoffeePrices(currentCart = null) {
      const items = Array.from(this.querySelectorAll('[data-rule="coffee"] [data-bundle-builder-item]'));
      if (!items.length) return;
      this.coffeePrices = {};
      this.coffeePriceError = false;
      this.render();
      try {
        const cart = currentCart || await this.fetchCart();
        const lines = new Map();
        items.forEach((item) => {
          const rule = this.ruleFor(item.closest('[data-bundle-builder-category]'));
          const options = Array.from(item.querySelectorAll('[data-bundle-builder-variant] option')).filter((o) => !o.disabled);
          const ids = options.length ? options.map((o) => o.value) : [item.dataset.variantId];
          ids.forEach((id) => lines.set(id, { variant_id: id, quantity: 1, rule: rule.claimRule, page: rule.page }));
        });
        const all = Array.from(lines.values());
        for (let offset = 0; offset < all.length; offset += 20) {
          const response = await fetch(this.config.pricesUrl, {
            method: 'POST', credentials: 'same-origin',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ bundle_id: 'cb_price_preview', machine_variant_id: this.machine.variantId,
              cart_token: normaliseToken(cart.token), company_location_id: this.config.companyLocationId || null,
              lines: all.slice(offset, offset + 20) })
          });
          const result = await response.json();
          if (!response.ok || !result.success) {
            const error = new Error('Coffee prices unavailable');
            if (response.status === 422 && typeof result.error === 'string') error.customerMessage = result.error;
            throw error;
          }
          for (const line of all.slice(offset, offset + 20)) {
            const quote = result.prices?.[line.variant_id];
            if (!quote || ![quote.price_cents, quote.bundle_price_cents, quote.b2b_price_cents].every(Number.isSafeInteger)
                || quote.bundle_price_cents < 0 || quote.bundle_price_cents > quote.price_cents) throw new Error('Invalid coffee price');
            this.coffeePrices[line.variant_id] = quote;
          }
        }
        items.forEach((item) => {
          const quote = this.coffeePrices[item.dataset.variantId];
          if (quote) item.dataset.price = String(quote.price_cents);
        });
      } catch (error) {
        this.coffeePrices = {};
        this.coffeePriceError = true;
        this.status(error.customerMessage || 'Coffee pricing is unavailable. Refresh the page to try again.', 'error');
      }
      this.render();
    }

    itemsIn(category) {
      return Array.from(category.querySelectorAll('[data-bundle-builder-item]'));
    }

    isAvailable(item) {
      const option = item.querySelector('[data-bundle-builder-variant]')?.selectedOptions?.[0];
      if (option) return !option.disabled;
      if (item.dataset.available !== undefined) return item.dataset.available !== 'false';
      return !item.querySelector('[data-bundle-builder-check]')?.disabled;
    }

    syncAvailability(item, selectionBlocked = false) {
      const available = this.isAvailable(item);
      const box = item.querySelector('[data-bundle-builder-check]');
      const dropped = !!(box?.checked && !available);
      item.dataset.available = String(available);
      item.classList.toggle('is-unavailable', !available);
      const label = item.querySelector('[data-bundle-builder-availability]');
      if (label) label.hidden = available;
      if (box) {
        box.disabled = !available || selectionBlocked;
        if (!available) box.checked = false;
      }
      if (!available) {
        const wrap = item.querySelector('[data-bundle-builder-qty-wrap]');
        if (wrap) wrap.hidden = true;
        const quantity = item.querySelector('[data-bundle-builder-qty]');
        if (quantity) quantity.value = '1';
      }
      return dropped;
    }

    isChecked(item) {
      const box = item.querySelector('[data-bundle-builder-check]');
      return !!(box && box.checked && !box.disabled && this.isAvailable(item));
    }

    selectedIn(category) {
      return this.itemsIn(category).filter((i) => this.isChecked(i));
    }

    get selectedItems() {
      return Array.from(this.querySelectorAll('[data-bundle-builder-item]')).filter((i) => this.isChecked(i));
    }

    lineFor(item) {
      const category = item.closest('[data-bundle-builder-category]');
      const rule = this.ruleFor(category);
      const qtyInput = item.querySelector('[data-bundle-builder-qty]');
      const freeCoffeeQuantity = Number(category.dataset.coffeeQuantity || 0);
      let quantity = [1, 2].includes(freeCoffeeQuantity) ? freeCoffeeQuantity : 1;
      if (!freeCoffeeQuantity && rule.allowQuantity && qtyInput) {
        const max = parseInt(qtyInput.max || '10', 10) || 10;
        quantity = Math.min(max, Math.max(1, parseInt(qtyInput.value, 10) || 1));
      }
      return {
        item,
        rule,
        discount: this.discountForItem(item),
        quantity,
        price: this.coffeePrices?.[item.dataset.variantId]?.price_cents ?? (parseInt(item.dataset.price || '0', 10) || 0),
        variantId: item.dataset.variantId,
        handle: item.dataset.productHandle,
        title: item.dataset.title || '',
        variantTitle: item.dataset.variantTitle || '',
        image: item.dataset.image || ''
      };
    }

    /* ---------- category tabs ---------- */

    wireTabs() {
      const tabs = this.tabs;
      if (!tabs.length) return;
      tabs.forEach((tab) => {
        this.listen(tab, 'click', () => this.activateTab(tab.dataset.bundleBuilderTab));
        this.listen(tab, 'keydown', (event) => {
          const index = tabs.indexOf(tab);
          let next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          this.activateTab(next.dataset.bundleBuilderTab);
          next.focus();
        });
      });
      const selected = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
      this.activateTab(selected.dataset.bundleBuilderTab, { scroll: false });
    }

    activateTab(id, { scroll = true } = {}) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.bundleBuilderTab === id;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (active && scroll && typeof tab.scrollIntoView === 'function') {
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.scrollBehavior });
        }
      });
      this.categories.forEach((category) => {
        category.hidden = category.dataset.bundleBuilderCategory !== id;
      });
      this.activeTabId = id;
    }

    observeStickyBar() {
      if (!this.hasAttribute('data-hide-sticky-bar')) return;
      const sticky = document.querySelector('product-sticky-form');
      if (!sticky || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver((entries) => {
        sticky.classList.toggle('bundle-builder-suppressed', entries.some((e) => e.isIntersecting));
      }, { rootMargin: '0px 0px -15% 0px', threshold: 0 });
      this.stickyObserver.observe(this);
    }

    /* ---------- selection ---------- */

    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.hasAttribute('data-bundle-builder-machine-select')) {
        const option = target.selectedOptions[0];
        if (option) this.setMachineVariant({
          id: option.value,
          price: Number(option.dataset.price),
          available: option.dataset.available === 'true' && !option.disabled,
          title: option.dataset.title
        }, option.dataset.image);
        return;
      }
      const item = target.closest('[data-bundle-builder-item]');
      if (!item) return;
      const category = item.closest('[data-bundle-builder-category]');
      const rule = this.ruleFor(category);

      if (target.hasAttribute('data-bundle-builder-check')) {
        if (target.checked && (category.dataset.max === '1' || category.hasAttribute('data-single-selection'))) this.keepSingleSelection(category, target);
        if (target.checked) this.status('');
        const qtyWrap = item.querySelector('[data-bundle-builder-qty-wrap]');
        if (qtyWrap) qtyWrap.hidden = !target.checked;
      }

      if (target.hasAttribute('data-bundle-builder-variant')) {
        const droppedSelection = this.syncVariant(item);
        if (droppedSelection) this.status(this.strings.unavailableVariant || '', 'info');
      }

      if (target.hasAttribute('data-bundle-builder-qty')) {
        const max = parseInt(target.max || '10', 10) || 10;
        const value = Math.min(max, Math.max(1, parseInt(target.value, 10) || 1));
        if (String(value) !== target.value) target.value = String(value);
      }

      this.render();
    }

    keepSingleSelection(category, selected) {
      category.querySelectorAll('[data-bundle-builder-check]:checked').forEach((box) => {
        if (box === selected) return;
        box.checked = false;
        const item = box.closest('[data-bundle-builder-item]');
        const wrap = item?.querySelector('[data-bundle-builder-qty-wrap]');
        if (wrap) wrap.hidden = true;
        const quantity = item?.querySelector('[data-bundle-builder-qty]');
        if (quantity) quantity.value = '1';
      });
    }

    // Copy the selected <option>'s data onto the row and refresh its price line.
    // Returns true when a checked row had to be unchecked because the variant is sold out.
    syncVariant(item) {
      const select = item.querySelector('[data-bundle-builder-variant]');
      if (!select) return false;
      const option = select.selectedOptions[0];
      if (!option) return false;
      item.dataset.variantId = option.value;
      item.dataset.price = option.dataset.price || '0';
      item.dataset.image = option.dataset.image || item.dataset.image;
      item.dataset.benchH = option.dataset.benchH || item.dataset.benchDefaultH || '';
      const thumb = item.querySelector('.bundle-builder__thumb img');
      if (thumb && item.dataset.image) {
        // Remove the original responsive source set so it cannot override the
        // selected variant's corrected image.
        thumb.removeAttribute('srcset');
        thumb.src = item.dataset.image;
      }
      item.dataset.variantTitle = option.dataset.title === 'Default Title' ? '' : (option.dataset.title || '');
      this.renderItemPrice(item);
      return this.syncAvailability(item);
    }

    /* ---------- category search ---------- */

    // Filters the category's rows by product title. Hidden rows keep their
    // checkbox state, so picked items stay in the package while filtered out.
    wireSearch() {
      this.querySelectorAll('[data-bundle-builder-search]').forEach((input) => {
        const category = input.closest('[data-bundle-builder-category]');
        if (!category) return;
        const apply = () => this.filterItems(category, input.value);
        this.listen(input, 'input', apply);
        this.listen(input, 'search', apply); // the clear (×) control
        if (input.value) apply(); // browser-restored state on soft reload
      });
    }

    filterItems(category, query) {
      const q = (query || '').trim().toLowerCase();
      let visible = 0;
      this.itemsIn(category).forEach((item) => {
        const match = !q || (item.dataset.title || '').toLowerCase().includes(q);
        item.hidden = !match;
        if (match) visible += 1;
      });
      const none = category.querySelector('[data-bundle-builder-no-matches]');
      if (none) none.hidden = visible > 0;
      const list = category.querySelector('.bundle-builder__items');
      if (list) this.syncListFades(list); // hidden rows change the scroll range
    }

    /* ---------- list edge fades ---------- */

    // Rows fade under the search bar once the list has scrolled, and above
    // the footer while rows remain below; at rest both edges are sharp. The
    // ResizeObserver catches a category being shown (its list goes from 0 to
    // its real height) so the initial state is right without a scroll.
    wireListFades() {
      this.querySelectorAll('.bundle-builder__items').forEach((list) => {
        const sync = () => this.syncListFades(list);
        this.listen(list, 'scroll', sync, { passive: true });
        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(sync);
          observer.observe(list);
          this.listObservers.push(observer);
        }
        sync();
      });
    }

    syncListFades(list) {
      const bottom = list.scrollTop + list.clientHeight;
      list.classList.toggle('is-scrolled', list.scrollTop > 1);
      list.classList.toggle('has-more-below', list.clientHeight > 0 && list.scrollHeight - bottom > 1);
    }

    /* ---------- custom variant dropdowns ---------- */

    // Replace the native <select> popup with a styled listbox. The hidden native
    // select stays the source of truth: picking an option sets select.value and
    // fires 'change', so onChange()/syncVariant() run unchanged - and without JS
    // the native control still works. Panels live on <body> with fixed
    // positioning so the scrollable item list can't clip them.
    enhanceVariantDropdowns() {
      this.variantDropdowns = [];
      this.querySelectorAll('[data-bundle-builder-variant]').forEach((select) => {
        const dropdown = this.buildVariantDropdown(select);
        if (dropdown) this.variantDropdowns.push(dropdown);
      });
    }

    buildVariantDropdown(select) {
      const field = select.closest('.bundle-builder__variant');
      if (!field || field.querySelector('.bundle-builder__variant-trigger')) return null;
      const ariaLabel = select.getAttribute('aria-label');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'select bundle-builder__variant-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      if (ariaLabel) trigger.setAttribute('aria-label', ariaLabel);
      const label = document.createElement('span');
      label.className = 'bundle-builder__variant-value';
      trigger.appendChild(label);
      // Keep the chevron inside the visible button. The field may shrink below
      // the button's minimum width, so a sibling icon can overlap the label.
      const chevron = field.querySelector('.icon');
      if (chevron) {
        const triggerChevron = chevron.cloneNode(true);
        triggerChevron.setAttribute('aria-hidden', 'true');
        trigger.appendChild(triggerChevron);
      }

      const panel = document.createElement('ul');
      panel.className = 'bundle-builder__variant-panel';
      panel.setAttribute('role', 'listbox');
      if (ariaLabel) panel.setAttribute('aria-label', ariaLabel);
      panel.hidden = true;

      Array.from(select.options).forEach((option) => {
        const li = document.createElement('li');
        li.className = 'bundle-builder__variant-option';
        li.setAttribute('role', 'option');
        li.dataset.value = option.value;
        li.textContent = option.textContent.trim();
        li.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        if (option.disabled) li.setAttribute('aria-disabled', 'true');
        li.tabIndex = -1;
        panel.appendChild(li);
      });

      const sync = () => {
        const current = select.selectedOptions[0];
        label.textContent = current ? current.textContent.trim() : '';
        panel.querySelectorAll('[role="option"]').forEach((li) => {
          li.setAttribute('aria-selected', li.dataset.value === select.value ? 'true' : 'false');
        });
      };

      const enabledOptions = () => Array.from(panel.querySelectorAll('[role="option"]:not([aria-disabled="true"])'));

      const onOutside = (event) => {
        if (panel.contains(event.target) || trigger.contains(event.target)) return;
        close();
      };
      const onScroll = (event) => { if (!panel.contains(event.target)) close(); };
      const onResize = () => close();

      const close = ({ focusTrigger = false } = {}) => {
        if (panel.hidden) return;
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', onOutside, true);
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
        if (openVariantDropdown === close) openVariantDropdown = null;
        if (focusTrigger) trigger.focus();
      };

      const position = () => {
        const rect = trigger.getBoundingClientRect();
        panel.style.minWidth = `${Math.ceil(rect.width)}px`;
        const width = panel.offsetWidth;
        const height = panel.offsetHeight;
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
        const openUp = window.innerHeight - rect.bottom < height + 8 && rect.top > height + 8;
        panel.style.left = `${left}px`;
        panel.style.top = `${openUp ? rect.top - height - 4 : rect.bottom + 4}px`;
      };

      const open = () => {
        if (!panel.hidden) return;
        if (openVariantDropdown) openVariantDropdown();
        openVariantDropdown = close;
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        position();
        document.addEventListener('pointerdown', onOutside, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        const active = panel.querySelector('[aria-selected="true"]:not([aria-disabled="true"])') || enabledOptions()[0];
        if (active) {
          // preventScroll: a page scroll here would hit the close-on-scroll
          // handler and shut the panel as it opens; scroll the panel instead.
          active.focus({ preventScroll: true });
          const panelRect = panel.getBoundingClientRect();
          const activeRect = active.getBoundingClientRect();
          if (activeRect.top < panelRect.top || activeRect.bottom > panelRect.bottom) {
            panel.scrollTop += activeRect.top - panelRect.top - (panelRect.height - activeRect.height) / 2;
          }
        }
      };

      const pick = (li) => {
        if (!li || li.getAttribute('aria-disabled') === 'true') return;
        if (select.value !== li.dataset.value) {
          select.value = li.dataset.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        close({ focusTrigger: true });
      };

      this.listen(trigger, 'click', () => (panel.hidden ? open() : close()));
      this.listen(trigger, 'keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          open();
        }
      });

      this.listen(panel, 'click', (event) => pick(event.target.closest('[role="option"]')));
      this.listen(panel, 'keydown', (event) => {
        const options = enabledOptions();
        const index = options.indexOf(document.activeElement);
        let next = null;
        if (event.key === 'ArrowDown') next = options[Math.min(index + 1, options.length - 1)];
        else if (event.key === 'ArrowUp') next = options[Math.max(index - 1, 0)];
        else if (event.key === 'Home') next = options[0];
        else if (event.key === 'End') next = options[options.length - 1];
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          pick(document.activeElement?.closest('[role="option"]'));
          return;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close({ focusTrigger: true });
          return;
        } else if (event.key === 'Tab') {
          close();
          return;
        } else return;
        if (next) { event.preventDefault(); next.focus(); }
      });

      this.listen(select, 'change', sync);
      sync();

      select.hidden = true;
      select.tabIndex = -1;
      field.classList.add('bundle-builder__variant--enhanced');
      // The original icon remains available for the native-select fallback.
      field.insertBefore(trigger, chevron);
      document.body.appendChild(panel);

      return { destroy: () => {
        close();
        panel.remove();
        trigger.remove();
        select.hidden = false;
        select.removeAttribute('tabindex');
        field.classList.remove('bundle-builder__variant--enhanced');
      } };
    }

    // Reset the whole selection before rendering so a large bundle does not
    // trigger one async bench layout and chip rebuild per removed item.
    clearAll() {
      this.querySelectorAll('[data-bundle-builder-check]:checked').forEach((box) => {
        box.checked = false;
      });
      this.querySelectorAll('[data-bundle-builder-qty-wrap]').forEach((wrap) => { wrap.hidden = true; });
      this.render();
      this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
    }

    isBenchProductVisible(line) {
      return !!this.sceneWrapper?.classList.contains('bundle-scene-active')
        && (!this.isMobile || !this.hiddenBenchProducts.has(line.item.dataset.productId))
        && this.visibleBenchKeys.has(`${line.item.dataset.productId}:${line.variantId}`);
    }

    syncVisibilityButtons() {
      const lines = this.lastPicked || [];
      this.pickedList?.querySelectorAll('[data-bundle-builder-visibility]').forEach((button) => {
        const line = lines.find((line) => line.item.dataset.productId === button.dataset.bundleBuilderVisibility);
        if (line) this.updateVisibilityButton(button, line);
      });
    }

    updateVisibilityButton(button, line) {
      const visible = this.isBenchProductVisible(line);
      button.setAttribute('aria-pressed', String(visible));
      const action = visible ? (this.strings.hideOnBench || 'Hide on bench') : (this.strings.showOnBench || 'Show on bench');
      button.setAttribute('aria-label', `${action}: ${line.title}`);
      button.title = action;
    }

    onPickedClick(event) {
      const visibility = event.target.closest('[data-bundle-builder-visibility]');
      if (visibility && this.isMobile) {
        const id = visibility.dataset.bundleBuilderVisibility;
        const line = (this.lastPicked || []).find((line) => line.item.dataset.productId === id);
        if (!line) return;
        if (this.isBenchProductVisible(line)) {
          this.hiddenBenchProducts.add(id);
          if (this.preferredBenchProduct === id) this.preferredBenchProduct = null;
        } else {
          this.hiddenBenchProducts.delete(id);
          // Give an explicitly requested product first use of the bench space.
          this.preferredBenchProduct = id;
        }
        this.updateVisibilityButton(visibility, line);
        this.renderBench(this.lastPicked);
        return;
      }
      const button = event.target.closest('[data-bundle-builder-unpick]');
      if (!button) return;
      const item = this.querySelector(`[data-bundle-builder-item][data-product-id="${button.dataset.bundleBuilderUnpick}"]`);
      const box = item?.querySelector('[data-bundle-builder-check]');
      if (!box) return;
      const index = Array.from(this.pickedList.querySelectorAll('[data-bundle-builder-unpick]')).indexOf(button);
      box.checked = false;
      box.dispatchEvent(new Event('change', { bubbles: true }));
      const remaining = this.pickedList.querySelectorAll('[data-bundle-builder-unpick]');
      const next = remaining[Math.min(index, remaining.length - 1)]
        || this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      next?.focus({ preventScroll: true });
      const panel = box.closest('[data-bundle-builder-category]');
      if (panel && !panel.hidden) {
        box.focus({ preventScroll: true });
      } else {
        const next = this.pickedList.querySelector('[data-bundle-builder-unpick]');
        (next || this.tabs[0])?.focus({ preventScroll: true });
      }
    }

    /* ---------- preview ---------- */

    discountFor(rule, line) {
      if ((!this.showSavings && !rule.coffee) || rule.type === 'none' || rule.value <= 0) return 0;
      // Shopify truncates ordinary percentage-code savings per unit, then multiplies quantity.
      if (rule.type === 'percentage') return Math.floor(line.price * rule.value / 100 + 1e-8) * line.quantity;
      if (rule.type === 'fixed_each') return Math.min(line.price, Math.round(rule.value * 100)) * line.quantity;
      return 0;
    }

    renderItemPrice(item) {
      const coffee = item.closest('[data-bundle-builder-category]')?.dataset.rule === 'coffee';
      const quote = this.coffeePrices?.[item.dataset.variantId];
      if (coffee && !quote) {
        const now = item.querySelector('[data-bundle-builder-item-now]');
        if (now) now.textContent = this.coffeePriceError ? 'Price unavailable' : 'Loading price…';
        const standalone = item.querySelector('[data-bundle-builder-item-standalone]');
        if (standalone) standalone.hidden = true;
        return;
      }
      if (coffee) item.dataset.price = String(quote.price_cents);
      const price = parseInt(item.dataset.price || '0', 10) || 0;
      const discount = this.discountFor(this.discountForItem(item), { price, quantity: 1 });
      const now = item.querySelector('[data-bundle-builder-item-now]');
      // Free 1kg coffee offer: the Workspace quotes the bag at zero.
      if (now) now.textContent = coffee && discount > 0 && price - discount <= 0 ? (this.strings.free || 'Free') : `+${money(price - discount)}`;
      const standalone = item.querySelector('[data-bundle-builder-item-standalone]');
      if (standalone) {
        standalone.hidden = discount <= 0;
        standalone.textContent = discount > 0 ? money(price) : '';
      }
    }

    render() {
      const machineInCart = this.isMachineInCart();
      let listTotal = machineInCart ? 0 : (parseInt(this.machine.price || 0, 10) || 0);
      let savingsTotal = 0;
      let addonCount = 0;
      let minSatisfied = true;
      const picked = [];

      this.categories.forEach((category) => {
        const rule = this.ruleFor(category);
        const items = this.itemsIn(category);
        items.forEach((item) => this.syncAvailability(item, rule.handle === 'coffee' && !this.coffeePrices?.[item.dataset.variantId]));
        if (category.dataset.max === '1' || category.hasAttribute('data-single-selection')) {
          this.keepSingleSelection(category, category.querySelector('[data-bundle-builder-check]:checked'));
        }
        const selected = items.filter((i) => this.isChecked(i));
        const lines = selected.map((i) => this.lineFor(i));
        const sub = lines.reduce((n, l) => n + l.price * l.quantity, 0);
        const saving = lines.reduce((n, l) => n + this.discountFor(l.discount, l), 0);
        listTotal += sub;
        savingsTotal += saving;
        addonCount += selected.length;
        if (selected.length < rule.min) minSatisfied = false;
        picked.push(...lines);

        // Show the bundle unit price above the current standalone price.
        items.forEach((item) => {
          this.renderItemPrice(item);
        });
      });

      this.renderPicked(picked);
      this.renderBench(picked);

      // The machine summary card shows only once something is in the package.
      const summary = this.querySelector('[data-bundle-builder-summary]');
      if (summary) summary.hidden = picked.length === 0;
      const inCartNote = this.querySelector('[data-bundle-builder-machine-in-cart]');
      if (inCartNote) inCartNote.hidden = !machineInCart;

      const machinePriceEl = this.querySelector('[data-bundle-builder-machine-price]');
      if (machinePriceEl) machinePriceEl.setAttribute('data-price', this.machine.price || 0);
      // The first totals row is the subtotal: machine plus every picked item
      // at full price, so subtotal - savings = total reads true.
      this.setText('[data-bundle-builder-machine-price]', money(listTotal));
      // Struck through once the bundle saves something (i.e. items are added).
      machinePriceEl?.classList.toggle('is-struck', savingsTotal > 0);
      this.renderSavings(savingsTotal);
      const total = listTotal - savingsTotal;
      this.setText('[data-bundle-builder-total]', money(total));

      if (this.submitButton) this.submitButton.disabled = addonCount === 0 || this.machine.available === false;
      this.scheduleMobileLayout();
      if (minSatisfied && this.statusEl && this.statusEl.textContent === (this.lastMinMessage || null)) this.status('');
    }

    renderPicked(lines) {
      if (!lines.some((line) => line.item.dataset.productId === this.preferredBenchProduct)) this.preferredBenchProduct = null;
      // Visibility belongs to the product, survives variant changes, and resets
      // when it is removed from the bundle.
      this.hiddenBenchProducts.forEach((id) => {
        if (!lines.some((line) => line.item.dataset.productId === id)) this.hiddenBenchProducts.delete(id);
      });
      if (!this.pickedWrap || !this.pickedList) return;
      this.pickedWrap.hidden = lines.length === 0;
      this.setText('[data-bundle-builder-selected-count]', fill(this.strings.selectedCount || '([count])', {
        count: lines.reduce((sum, line) => sum + line.quantity, 0)
      }));
      if (!lines.length) this.summaryToggle?.setAttribute('aria-expanded', 'false');
      const prevScroll = this.pickedList.scrollTop;
      const prevCount = this.pickedCount || 0;
      this.pickedList.textContent = '';
      lines.forEach((line) => {
        const li = document.createElement('li');
        li.className = 'bundle-builder__picked-item';
        const img = document.createElement('img');
        img.src = line.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 64; img.height = 64;
        const label = document.createElement('span');
        label.className = 'bundle-builder__picked-label';
        label.textContent = line.quantity > 1 ? `${line.quantity} × ${line.title}` : line.title;
        if (line.variantTitle) {
          const variant = document.createElement('span');
          variant.className = 'bundle-builder__picked-variant';
          variant.textContent = line.variantTitle;
          label.appendChild(variant);
        }
        const visibility = document.createElement('button');
        visibility.type = 'button';
        visibility.className = 'bundle-builder__picked-visibility';
        visibility.dataset.bundleBuilderVisibility = line.item.dataset.productId;
        visibility.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/><path class="bundle-builder__eye-slash" d="m3 3 18 18"/></svg>';
        this.updateVisibilityButton(visibility, line);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-builder__picked-remove';
        remove.setAttribute('aria-label', `${this.strings.remove || 'Remove'}: ${line.title}`);
        remove.dataset.bundleBuilderUnpick = line.item.dataset.productId;
        remove.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18"/></svg>';
        li.title = line.variantTitle ? `${line.title} (${line.variantTitle})` : line.title;
        const removeText = document.createElement('span');
        removeText.className = 'bundle-builder__picked-remove-text';
        removeText.setAttribute('aria-hidden', 'true');
        removeText.textContent = '×';
        remove.appendChild(removeText);
        const actions = document.createElement('span');
        actions.className = 'bundle-builder__picked-actions';
        actions.append(visibility, remove);
        li.append(img, label, actions);
        this.pickedList.appendChild(li);
      });
      // The list shows two rows and scrolls beyond that (CSS max-height). A new
      // pick scrolls the newest row into view, pushing the first row up; other
      // re-renders (qty edits, variant swaps) keep the reader's position. Applied
      // again next frame because the summary card may still be hidden on the
      // very first pick, when scroll metrics read as zero.
      this.pickedCount = lines.length;
      const settle = () => {
        if (!this.pickedList) return;
        this.pickedList.scrollTop = this.isMobile ? 0 : (lines.length > prevCount ? this.pickedList.scrollHeight : prevScroll);
        this.syncPickedScrollState();
      };
      settle();
      requestAnimationFrame(settle);
    }

    // One chip row + gap, measured from the live grid (fallback: 2.625rem + 0.5rem).
    pickedRowStep() {
      const chips = this.pickedList ? this.pickedList.querySelectorAll('.bundle-builder__picked-item') : [];
      for (let i = 1; i < chips.length; i += 1) {
        const step = chips[i].offsetTop - chips[0].offsetTop;
        if (step > 0) return step;
      }
      return chips[0] ? chips[0].offsetHeight + 8 : 50;
    }

    scrollPickedRows(rows) {
      if (!this.pickedList) return;
      this.pickedList.scrollBy({ top: rows * this.pickedRowStep(), behavior: this.scrollBehavior });
    }

    // Edge fades + the "N more above" pill: a chip counts as out of view when
    // its midline is above the list's top edge (the sliver of a pushed-up row
    // stays visible on purpose, so it must not read as "in view"). Rows below
    // only get the bottom fade, no pill.
    syncPickedScrollState() {
      const list = this.pickedList;
      if (!list) return;
      if (this.isMobile) {
        this.pickedScroll?.classList.remove('is-scrolled', 'has-more-below');
        if (this.pickedMoreUp) this.pickedMoreUp.hidden = true;
        return;
      }
      const top = list.scrollTop;
      const bottom = top + list.clientHeight;
      let above = 0;
      list.querySelectorAll('.bundle-builder__picked-item').forEach((chip) => {
        if (chip.offsetTop + chip.offsetHeight / 2 < top) above += 1;
      });
      if (this.pickedScroll) {
        this.pickedScroll.classList.toggle('is-scrolled', top > 1);
        this.pickedScroll.classList.toggle('has-more-below', list.scrollHeight - bottom > 1);
      }
      if (this.pickedMoreUp) {
        this.pickedMoreUp.hidden = above === 0;
        if (above) this.pickedMoreUp.textContent = (this.strings.moreAbove || '↑ [count] more above').replace('[count]', String(above));
      }
    }

    /* ---------- bench scene ---------- */

    // The scene uses wrapper coordinates on desktop and the same photo-space
    // coordinates inside a cropped in-flow frame on mobile. Sprites
    // are decorative (the layer is aria-hidden, pointer-events: none); the
    // chips list stays the interactive record. CB-T9 renders the machine;
    // add-on sprites land in CB-T10 via the same renderBench(lines) funnel.
    initBenchScene() {
      const wrapper = this.closest('.commercial-bundle-builder-section');
      this.sceneEl = wrapper ? wrapper.querySelector('[data-bundle-builder-scene]') : null;
      if (!this.sceneEl) return;
      this.sceneWrapper = wrapper;
      this.sceneFrame = this.querySelector('[data-bundle-builder-scene-frame]');
      this.sceneAnchor = document.createComment('bundle bench desktop position');
      this.sceneEl.before(this.sceneAnchor);
      this.benchSprites = new Map();
      this.benchDebug = /[?&]bench_debug=/.test(window.location.search);
      if (this.benchDebug) this.sceneEl.classList.add('bundle-builder__scene--debug');
      this.positionBenchScene();
    }

    get benchActive() {
      return !!(this.isConnected && this.sceneEl);
    }

    get benchCalibration() {
      return this.isMobile ? { ...BENCH, spanMin: 250, spanMax: 930, pxPerMm: 0.48 } : BENCH;
    }

    positionBenchScene() {
      if (!this.sceneEl || !this.sceneFrame) return;
      if (this.isMobile) this.sceneFrame.appendChild(this.sceneEl);
      else this.sceneAnchor.after(this.sceneEl);
      this.sceneEl.querySelector('[data-bench-debug]')?.remove();
    }

    frameMobileBench(lines) {
      if (!this.isMobile || !this.sceneFrame) return;
      const tallest = Math.max(this.config.machine.benchHeightMm || 500, ...lines.map((line) =>
        parseFloat(line.item.dataset.benchH) || BENCH_FALLBACK_MM[line.rule.handle] || 250));
      // Keep the bench at the same scale and reveal more headroom for tall art.
      const top = Math.min(130, Math.max(0, 508 - tallest * this.benchCalibration.pxPerMm - 32));
      const height = 592.5 - top;
      this.sceneFrame.style.setProperty('--bundle-scene-ratio', String(740 / height));
      this.sceneFrame.style.setProperty('--bundle-scene-top', `${-top / height * 100}%`);
    }

    renderBench(lines) {
      this.lastPicked = lines;
      if (!this.sceneEl) return;
      if (!this.benchActive) {
        this.clearBench();
        return;
      }
      if (this.benchDebug) this.renderBenchDebug();
      const visibleLines = this.isMobile ? lines.filter((line) => !this.hiddenBenchProducts.has(line.item.dataset.productId)) : lines;
      this.frameMobileBench(visibleLines);
      this.renderBenchMachine();
      this.renderBenchAddons(visibleLines);
    }

    clearBench() {
      this.benchLayoutToken = (this.benchLayoutToken || 0) + 1;
      this.benchSprites.forEach((sprite) => sprite.el.remove());
      this.benchSprites.clear();
      this.visibleBenchKeys.clear();
      this.syncVisibilityButtons();
      this.sceneEl.querySelector('[data-bench-debug]')?.remove();
      this.sceneWrapper.classList.remove('bundle-scene-active');
      if (this.sceneAnchor?.isConnected) this.sceneAnchor.after(this.sceneEl);
      this.sceneAnchor?.remove();
    }

    machineBenchSrc() {
      const img = this.querySelector('.bundle-builder__preview-img');
      const src = img && img.getAttribute('src');
      // Query params stripped for a stable measurement cache key, then a single
      // width for consistent quality (the variantChange subscriber swaps the
      // preview src, so this follows the selected machine variant for free).
      return src ? this.resize(src.split('?')[0], 1200) : '';
    }

    renderBenchMachine() {
      const src = this.machineBenchSrc();
      if (!src) return;
      let sprite = this.benchSprites.get('machine');
      if (!sprite) {
        sprite = this.createBenchSprite('bundle-builder__sprite--machine');
        this.benchSprites.set('machine', sprite);
        this.sceneEl.appendChild(sprite.el);
      }
      sprite.src = src;
      measureBenchBBox(src).then((bbox) => {
        if (sprite.src !== src || !this.benchActive || this.benchSprites.get('machine') !== sprite) return;
        if (bbox.failed) {
          this.sceneWrapper.classList.remove('bundle-scene-active');
          this.syncVisibilityButtons();
          return;
        }
        if (sprite.img.getAttribute('src') !== src) sprite.img.src = src;
        this.placeBenchSprite(sprite, bbox, this.benchCalibration.machineX, this.config.machine.benchHeightMm || 500);
        // Swap the in-flow preview for the scene only once the sprite is real -
        // no empty-bench flash while the first measurement runs.
        this.sceneWrapper.classList.add('bundle-scene-active');
        this.syncVisibilityButtons();
      });
    }

    createBenchSprite(extraClass) {
      const el = document.createElement('div');
      el.className = `bundle-builder__sprite${extraClass ? ` ${extraClass}` : ''}`;
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      el.appendChild(img);
      return { el, img, src: '' };
    }

    // Sprite wrapper = the content bbox; the inner img is oversized/offset so
    // the bbox exactly fills the wrapper. The physical height maps bbox top to
    // the SUPPORT BASELINE (feet/drip tray), which sits on the arris; anything
    // below the baseline (a dangling portafilter handle) renders below the
    // bench line, like hardware overhanging the bench's front edge. All values
    // are percentages of the scene layer, so nothing recomputes on resize.
    placeBenchSprite(sprite, bbox, xCenter, heightMm) {
      const bench = this.benchCalibration;
      const bw = Math.max(0.01, bbox.x1 - bbox.x0);
      const bhFull = Math.max(0.01, bbox.y1 - bbox.y0);
      const base = Math.min(Math.max(bbox.base || bbox.y1, bbox.y0 + 0.005), bbox.y1);
      const bhBody = Math.max(0.005, base - bbox.y0);
      const hBody = heightMm * bench.pxPerMm; // feet-to-top, img px
      const hFull = hBody * (bhFull / bhBody); // incl. below-baseline protrusion
      const w = hFull * (bbox.aspect || 1); // width from the bbox - spec width lies for angled shots
      const y = BENCH.arris(xCenter) + (hFull - hBody); // wrapper bottom dips below the arris
      sprite.el.style.left = `${(((xCenter - w / 2) / BENCH.imgW) * 100).toFixed(3)}%`;
      sprite.el.style.bottom = `${(((BENCH.imgH - y) / BENCH.imgH) * 100).toFixed(3)}%`;
      sprite.el.style.width = `${((w / BENCH.imgW) * 100).toFixed(3)}%`;
      sprite.el.style.height = `${((hFull / BENCH.imgH) * 100).toFixed(3)}%`;
      sprite.img.style.left = `${((-bbox.x0 / bw) * 100).toFixed(3)}%`;
      sprite.img.style.top = `${((-bbox.y0 / bhFull) * 100).toFixed(3)}%`;
      sprite.img.style.width = `${((1 / bw) * 100).toFixed(3)}%`;
      sprite.img.style.height = `${((1 / bhFull) * 100).toFixed(3)}%`;
      sprite.el.classList.toggle('bundle-builder__sprite--blend', !!bbox.opaque);
      void sprite.el.offsetWidth; // style flush so the entrance transition runs
      sprite.el.classList.add('is-placed');
    }

    // One sprite per picked line (Q17), keyed product:variant so a variant
    // image swap reads as remove+add. Packing needs every content width, and
    // widths come from async bbox measurement, so layout runs once the whole
    // set (machine included - its edges anchor the packing) has resolved; a
    // token discards stale resolutions when picks change mid-measure.
    renderBenchAddons(lines) {
      const jobs = [];
      // Viewed from the front: Jura's milk connection is on the right; WMF's
      // is on the left. Keep the existing left-side default for other brands.
      const milkSide = (this.config.machine.handle || '').startsWith('jura-') ? 'right' : 'left';
      lines.forEach((line) => {
        if (!line.image) return; // no art - the chip is the only record
        const rule = line.rule.handle || line.rule.id || '';
        const milkCooling = [
          'jura-cool-control-1-litre',
          'jura-cool-control-2-5-litre',
          'milk-fridge-for-coffee-machine-2-ctn',
          'milk-fridge-for-coffee-machine-1-x-2-litre'
        ].includes(line.item.dataset.productHandle);
        jobs.push({
          key: `${line.item.dataset.productId}:${line.variantId}`,
          src: this.resize(line.image.split('?')[0], 400),
          heightMm: parseFloat(line.item.dataset.benchH)
            || BENCH_FALLBACK_MM[rule]
            || BENCH_FALLBACK_MM.default,
          // Loose tools rest on their lowest visible point, not the broad-row
          // support heuristic used for machine feet and projecting trays.
          groundAtBottom: ['rocket-bottomless-portafilter', 'rhino-short-thermometer',
            'cafetto-milk-cleaning-brush-set'].includes(line.item.dataset.productHandle),
          grinder: rule === 'grinders' && !milkCooling,
          side: milkCooling ? milkSide : null
        });
      });
      // Decorative empty-state silhouettes use the same measured outlines and
      // bench scale as real products; they never enter selections or pricing.
      if (!(this.lastPicked || lines).length) {
        (this.config.benchPlaceholders || []).forEach((placeholder) => {
          if (!placeholder.src) return;
          jobs.push({
            key: `placeholder:${placeholder.id}`,
            src: placeholder.src,
            heightMm: placeholder.heightMm,
            grinder: !!placeholder.grinder,
            side: placeholder.side,
            placeholder: true
          });
        });
      }
      const machineSrc = this.machineBenchSrc();
      const token = (this.benchLayoutToken = (this.benchLayoutToken || 0) + 1);
      Promise.all([
        machineSrc ? measureBenchBBox(machineSrc) : Promise.resolve(null),
        ...jobs.map((job) => measureBenchBBox(job.src).then((bbox) => ({
          ...job, bbox: job.groundAtBottom ? { ...bbox, base: bbox.y1 } : bbox
        })))
      ]).then(([machineBBox, ...measured]) => {
        if (token !== this.benchLayoutToken || !this.benchActive) return;
        this.layoutBenchAddons(measured.filter((job) => !job.bbox.failed), machineBBox, lines);
      });
    }

    layoutBenchAddons(jobs, machineBBox, lines) {
      const bench = this.benchCalibration;
      const mmPx = bench.pxPerMm;
      // Packing uses the same support-baseline correction as sprite placement.
      const aspect = (bbox) => {
        if (!bbox) return 1.4;
        const full = Math.max(0.01, bbox.y1 - bbox.y0);
        const base = Math.min(Math.max(bbox.base || bbox.y1, bbox.y0 + 0.005), bbox.y1);
        return (bbox.aspect || 1) * full / Math.max(0.005, base - bbox.y0);
      };
      const machineH = (this.config.machine.benchHeightMm || 500) * mmPx;
      const machineW = machineH * aspect(machineBBox);
      const gap = bench.gapMm * mmPx;
      // Balanced still-life placement. Each side is a run outward from the
      // machine with the tallest item nearest it and the flattest at the end,
      // so the skyline falls away on both sides whatever is picked. Which
      // side an item goes on is chosen so the two sides occupy about the
      // same length of bench (widest items decided first, LPT-style). Length,
      // not silhouette area: area grows with the square of height, so one
      // hoppered grinder "outweighed" four smaller items and everything
      // piled up on the left. Milk cooling stays on its machine-specific side;
      // grinders take the right, and
      // empty-state silhouettes keep their scripted side. Sides are sticky:
      // an item keeps the side it was given, so a new pick normally reorders
      // only its own side. When the two sides differ by more than
      // bench.balanceMm of bench length (a late grinder, a removed item),
      // the single crossing that best restores balance is made, repeated
      // only while still over tolerance - so the bench never drifts into
      // five-against-one, but a change rarely moves more than one item
      // across the machine. An item that fits on neither side is not drawn -
      // the chips stay authoritative.
      const width = (job) => job.heightMm * mmPx * aspect(job.bbox);
      const edge = {
        right: bench.machineX + machineW / 2 + gap,
        left: bench.machineX - machineW / 2 - gap
      };
      const room = { right: bench.spanMax - edge.right, left: edge.left - bench.spanMin };
      const other = (side) => (side === 'left' ? 'right' : 'left');

      // Lay a side out: tallest first, outward. Items that don't fit are
      // skipped without advancing the cursor so a narrower one behind them
      // can still land. Serves both the balance test and the final pass.
      const run = (side, list) => {
        const out = [];
        let cursor = 0; // distance from this side's machine edge, img px
        const preferred = (job) => this.isMobile && job.key.startsWith(`${this.preferredBenchProduct}:`);
        list.slice().sort((a, b) => Number(!!b.side) - Number(!!a.side) || Number(preferred(b)) - Number(preferred(a)) || b.heightMm - a.heightMm).forEach((job) => {
          const w = width(job);
          if (cursor + w > room[side]) {
            out.push({ job, w, fits: false });
            return;
          }
          out.push({ job, w, dist: cursor + w / 2, fits: true });
          cursor += w + gap;
        });
        return { out, cursor };
      };
      const extent = (side, list) => run(side, list).cursor;

      if (!this.benchSideMemory) this.benchSideMemory = new Map();
      const memory = this.benchSideMemory;
      const sides = { left: [], right: [] };
      const forcedSide = (job) => job.side || (job.grinder ? 'right' : null);
      const assign = (job) => {
        const curLeft = extent('left', sides.left);
        const curRight = extent('right', sides.right);
        const ifLeft = Math.abs(extent('left', sides.left.concat(job)) - curRight);
        const ifRight = Math.abs(curLeft - extent('right', sides.right.concat(job)));
        sides[ifLeft <= ifRight ? 'left' : 'right'].push(job);
      };
      // Fixed-side and remembered items first, then the new ones widest-first
      // so the big decisions are made while both sides are open.
      const settled = (job) => forcedSide(job) || memory.get(job.key) || null;
      jobs.forEach((job) => {
        const side = settled(job);
        if (side) sides[side].push(job);
      });
      jobs
        .filter((job) => !settled(job))
        .sort((a, b) => width(b) - width(a))
        .forEach(assign);
      // Rebalance by single crossings while over tolerance.
      const tolerance = bench.balanceMm * mmPx;
      for (let pass = 0; pass < 3; pass += 1) {
        const left = extent('left', sides.left);
        const right = extent('right', sides.right);
        if (Math.abs(left - right) <= tolerance) break;
        const heavy = left > right ? 'left' : 'right';
        const light = other(heavy);
        let best = null;
        sides[heavy].forEach((job) => {
          if (forcedSide(job)) return;
          const rest = sides[heavy].filter((j) => j !== job);
          const gapAfter = Math.abs(extent(heavy, rest) - extent(light, sides[light].concat(job)));
          if (gapAfter < Math.abs(left - right) && (!best || gapAfter < best.gapAfter)) best = { job, gapAfter };
        });
        if (!best) break;
        sides[heavy] = sides[heavy].filter((j) => j !== best.job);
        sides[light].push(best.job);
      }

      const placed = new Map();
      const cursors = {};
      const overflow = [];
      ['right', 'left'].forEach((side) => {
        const { out, cursor } = run(side, sides[side]);
        cursors[side] = cursor;
        out.forEach((p) => {
          if (!p.fits) {
            overflow.push({ job: p.job, from: side });
            return;
          }
          const x = side === 'right' ? edge.right + p.dist : edge.left - p.dist;
          placed.set(p.job.key, { ...p.job, xCenter: x });
          if (!p.job.placeholder) memory.set(p.job.key, side);
        });
      });
      // A side is full: the far end of the other side beats vanishing. Not
      // remembered, so the item returns to its balanced side once room frees.
      overflow.forEach(({ job, from }) => {
        if (job.side) return; // Milk cooling and other explicit placements never cross the machine.
        const side = other(from);
        const w = width(job);
        if (cursors[side] + w > room[side]) return;
        const dist = cursors[side] + w / 2;
        cursors[side] += w + gap;
        placed.set(job.key, { ...job, xCenter: side === 'right' ? edge.right + dist : edge.left - dist });
      });
      memory.forEach((_, key) => {
        if (!jobs.some((job) => job.key === key)) memory.delete(key);
      });
      const missing = lines.filter((line) => !placed.has(`${line.item.dataset.productId}:${line.variantId}`)).length;
      const overflowButton = this.querySelector('[data-bundle-builder-scene-overflow]');
      if (overflowButton) {
        overflowButton.hidden = missing === 0;
        overflowButton.textContent = fill(this.strings.sceneOverflow || '+[count] more selected (view bundle)', { count: missing });
      }
      // Reconcile: drop sprites that are gone or no longer fit…
      this.benchSprites.forEach((sprite, key) => {
        if (key === 'machine' || placed.has(key)) return;
        this.benchSprites.delete(key);
        sprite.el.classList.remove('is-placed');
        setTimeout(() => sprite.el.remove(), 300);
      });
      // …then create/refresh the survivors. placeBenchSprite is cheap and
      // idempotent; repositions animate via the .is-placed transition.
      placed.forEach((job, key) => {
        let sprite = this.benchSprites.get(key);
        if (!sprite) {
          sprite = this.createBenchSprite('bundle-builder__sprite--addon');
          this.benchSprites.set(key, sprite);
          this.sceneEl.appendChild(sprite.el);
        }
        if (sprite.src !== job.src) {
          sprite.src = job.src;
          sprite.img.src = job.src;
        }
        sprite.el.classList.toggle('bundle-builder__sprite--placeholder', !!job.placeholder);
        this.placeBenchSprite(sprite, job.bbox, job.xCenter, job.heightMm);
      });
      this.visibleBenchKeys = new Set(placed.keys());
      this.syncVisibilityButtons();
    }

    // ?bench_debug=1 - calibration overlay in natural image coordinates.
    renderBenchDebug() {
      const bench = this.benchCalibration;
      if (this.sceneEl.querySelector('[data-bench-debug]')) return;
      const ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', `0 0 ${bench.imgW} ${bench.imgH}`);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.setAttribute('data-bench-debug', '');
      const add = (name, attrs, text) => {
        const node = document.createElementNS(ns, name);
        Object.keys(attrs).forEach((k) => node.setAttribute(k, attrs[k]));
        if (text) node.textContent = text;
        svg.appendChild(node);
      };
      const green = { stroke: '#00e676', 'stroke-width': '2', fill: 'none' };
      const blue = { stroke: '#2979ff', 'stroke-width': '2', fill: 'none' };
      // Bench line (arris) across the photo; span ticks; machine anchor.
      add('line', { ...green, x1: 0, y1: bench.arris(0), x2: bench.imgW, y2: bench.arris(bench.imgW) });
      [bench.spanMin, bench.spanMax].forEach((x) => {
        add('line', { ...green, 'stroke-dasharray': '6 6', x1: x, y1: bench.arris(x) - 120, x2: x, y2: bench.arris(x) + 20 });
      });
      add('line', { ...green, stroke: '#ff9100', x1: bench.machineX, y1: bench.arris(bench.machineX) - 60, x2: bench.machineX, y2: bench.arris(bench.machineX) });
      // 600 mm ruler below the bench line.
      const rx = 200;
      const ry = bench.arris(rx) + 60;
      const rw = 600 * bench.pxPerMm;
      add('line', { ...blue, x1: rx, y1: ry, x2: rx + rw, y2: ry });
      add('line', { ...blue, x1: rx, y1: ry - 10, x2: rx, y2: ry + 10 });
      add('line', { ...blue, x1: rx + rw, y1: ry - 10, x2: rx + rw, y2: ry + 10 });
      add('text', { x: rx + rw / 2, y: ry + 34, fill: '#2979ff', 'font-size': '24', 'text-anchor': 'middle' }, `600 mm @ ${bench.pxPerMm} px/mm`);
      this.sceneEl.appendChild(svg);
    }

    cancelSavingsAnimation() {
      if (this.savingsFrame != null) cancelAnimationFrame(this.savingsFrame);
      this.savingsFrame = null;
      if (this.savingsAnimatedEl) {
        this.savingsAnimatedEl.style.transform = '';
        this.savingsAnimatedEl.style.opacity = '';
        this.savingsAnimatedEl = null;
      }
    }

    renderSavings(amount) {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      const el = this.querySelector('[data-bundle-builder-bundle-savings]');
      const paint = (value) => {
        this.displayedSavings = value;
        this.setText('[data-bundle-builder-bundle-savings]', (value > 0 ? '-' : '') + money(value));
      };
      if (this.displayedSavings == null || reducedMotion?.matches || !el) {
        this.cancelSavingsAnimation();
        this.savingsTarget = amount;
        paint(amount);
        return;
      }
      if (this.savingsTarget === amount) return;
      this.cancelSavingsAnimation();
      this.savingsTarget = amount;
      const from = this.displayedSavings;
      if (from === amount) return;
      const direction = amount > from ? 1 : -1;
      this.savingsAnimatedEl = el;
      const started = performance.now();
      const tick = (now) => {
        const progress = reducedMotion?.matches ? 1 : Math.min(1, (now - started) / 180);
        // One flip of the entire price; switch values only at its hidden midpoint.
        const outgoing = progress < 0.5;
        const fold = outgoing ? progress * 2 : (1 - progress) * 2;
        const sign = outgoing ? -direction : direction;
        if (!outgoing && this.displayedSavings !== amount) paint(amount);
        el.style.transform = `perspective(240px) translateY(${sign * fold * 3}px) rotateX(${sign * fold * 90}deg)`;
        el.style.opacity = String(1 - fold);
        if (progress < 1) this.savingsFrame = requestAnimationFrame(tick);
        else this.cancelSavingsAnimation();
      };
      this.savingsFrame = requestAnimationFrame(tick);
    }

    setText(selector, text) {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    }

    resize(src, size) {
      // Theme assets (bench cut-outs in assets/) are served as-is; only product
      // media goes through the CDN's width transform.
      if (src.includes('/assets/')) return src.replace(/\s/g, '');
      return `${src}${src.includes('?') ? '&' : '?'}width=${size}`.replace(/\s/g, '');
    }

    status(message, tone = 'info') {
      if (!this.statusEl) return;
      if (!message) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        this.statusEl.className = 'bundle-builder__status alert text-sm font-medium leading-tight text-center';
        return;
      }
      this.statusEl.className = `bundle-builder__status alert alert--${tone} text-sm font-medium leading-tight text-center`;
      this.statusEl.textContent = message;
      this.statusEl.hidden = false;
      this.statusEl.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    }

    error(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    busy(flag) {
      if (!this.submitButton) return;
      if (flag) {
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    /* ---------- machine already in the cart ---------- */

    isMachineInCart() {
      return (this.cartItems || []).some((i) => String(i.variant_id) === String(this.machine.variantId));
    }

    async syncMachineInCart() {
      try {
        this.cartItems = (await this.fetchCart()).items || [];
      } catch (err) {
        return;
      }
      this.render();
    }

    get isSetupTarget() {
      return document.querySelector('commercial-bundle-builder') === this;
    }

    // Same page and variant: close the drawer and jump instead of reloading.
    async onSetupLinkClick(event) {
      const link = event.target.closest?.('[data-cart-setup-link]');
      if (!link || !this.isSetupTarget) return;
      const url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname) return;
      const variant = url.searchParams.get('variant');
      if (variant && variant !== String(this.machine.variantId)) return;
      event.preventDefault();
      // The drawer keeps the page scroll-locked and hands focus back to the cart
      // icon a tick after it closes; scrolling before that doesn't stick.
      // Capped, in case the close transition never reports back.
      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      await Promise.race([this.cartDrawer?.hide(), wait(700)]);
      await wait(60);
      this.goToSetup();
    }

    openFromHash() {
      if (window.location.hash === '#build-your-setup' && this.isSetupTarget) this.goToSetup();
    }

    // Jump rather than smooth-scroll: lazy images, the 3D model and late sections
    // above the builder change height while a smooth scroll is under way, so it
    // lands short and lurches. Then keep correcting for at least 3s and until the
    // builder has stayed put for a second (max 6s), or the shopper scrolls. A timer,
    // not ResizeObserver, so scroll restores and focus moves are caught too.
    goToSetup() {
      this.releaseSetupHold?.();
      this.scrollToSetup();
      const controller = new AbortController();
      const started = Date.now();
      let steadySince = Date.now();
      const timer = setInterval(() => {
        if (Math.abs(this.setupOffset()) > 4) {
          this.scrollToSetup();
          steadySince = Date.now();
        } else if ((Date.now() - started > 3000 && Date.now() - steadySince > 1000) || Date.now() - started > 6000) {
          this.releaseSetupHold();
        }
      }, 100);
      this.releaseSetupHold = () => {
        clearInterval(timer);
        controller.abort();
        this.releaseSetupHold = null;
      };
      const opts = { passive: true, signal: controller.signal };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) => window.addEventListener(type, () => this.releaseSetupHold?.(), opts));
    }

    // Distance from where the builder should sit (just under the header).
    setupOffset() {
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return this.getBoundingClientRect().top - headerHeight - 16;
    }

    scrollToSetup() {
      window.scrollTo({ top: Math.max(0, window.scrollY + this.setupOffset()), behavior: 'instant' });
    }

    /* ---------- add to cart ---------- */

    async fetchCart() {
      const response = await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') });
      return response.json();
    }

    bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      return sections;
    }

    minMessage() {
      for (const category of this.categories) {
        const rule = this.ruleFor(category);
        if (this.selectedIn(category).length < rule.min) {
          return fill(this.strings.minItems || '', { count: rule.min, label: rule.label });
        }
      }
      return '';
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.submitButton || this.submitButton.hasAttribute('aria-disabled') || this.submitButton.disabled) return;
      const minMessage = this.minMessage();
      if (minMessage) { this.lastMinMessage = minMessage; this.status(minMessage, 'error'); return; }

      this.error('');
      this.status('');
      this.busy(true);

      try {
        const cartBefore = await this.fetchCart();
        const hasCoffee = this.selectedItems.some((item) => item.closest('[data-bundle-builder-category]')?.dataset.rule === 'coffee');
        if (hasCoffee) {
          await this.loadCoffeePrices(cartBefore);
          if (this.coffeePriceError) return;
        }
        const machineInCart = (cartBefore.items || []).some((i) => String(i.variant_id) === String(this.machine.variantId));
        // One bundle id per cart: the Workspace supersedes claims per cart token, so a
        // second package must extend the first (same id → identical properties → lines
        // merge) and the claim must describe every tagged add-on in the cart.
        const existingId = this.existingBundleId(cartBefore);
        const bundleId = existingId || newBundleId();
        const pkg = this.config.packageLabel || this.machine.title;
        const lines = this.selectedItems.map((i) => this.lineFor(i));

        const items = [];
        if (!machineInCart) {
          items.push({ id: this.machine.variantId, quantity: 1, properties: { _bundle_id: bundleId, _bundle_role: 'machine', Package: pkg } });
        }
        lines.forEach((line) => {
          items.push({
            id: line.variantId,
            quantity: line.quantity,
            properties: { _bundle_id: bundleId, _bundle_role: 'addon', _bundle_cat: line.rule.claimRule || line.rule.handle || line.rule.id,
              ...(line.rule.page ? { _bundle_page: line.rule.page.path, _bundle_section: line.rule.page.section } : {}),
              Package: pkg }
          });
        });

        const addResponse = await fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items, sections: this.bundledSections(), sections_url: window.location.pathname })
        });
        const parsed = await addResponse.json();

        if (parsed.status) {
          this.error(parsed.description || parsed.message || this.strings.addError);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsed.description } }));
          const cartJson = await this.fetchCart();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'commercial-bundle-builder', cart: cartJson });
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          return;
        }

        let cartJson = await this.fetchCart();
        cartJson.sections = parsed.sections;

        if (this.enforcement === 'codes') {
          const fallback = lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity, rule: l.rule.claimRule || l.rule.handle || l.rule.id, page: l.rule.page }));
          const updated = await this.applyCodes({ bundleId, cartJson, lines: this.taggedAddonLines(cartJson, fallback) });
          if (updated) cartJson = updated;
        }

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'commercial-bundle-builder', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));
        document.dispatchEvent(new CustomEvent('bundle-builder:added', { bubbles: true, detail: { bundleId, cart: cartJson } }));

        this.cartDrawer?.show(this.submitButton);
        this.reset();
      } catch (err) {
        console.error('[bundle-builder]', err);
        this.error(this.strings.addError);
      } finally {
        this.busy(false);
      }
    }

    existingBundleId(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const machine = items.find((i) => String(i.variant_id) === String(this.machine.variantId) && i.properties?._bundle_id);
      if (machine) return machine.properties._bundle_id;
      const any = items.find((i) => i.properties?._bundle_id);
      return any ? any.properties._bundle_id : null;
    }

    // Every add-on line in the cart that carries package properties, merged by
    // variant + rule (Shopify already merges identical property sets, but the cart
    // may hold lines from an earlier package with a different id). Falls back to
    // what was just picked if the cart read is unusable.
    taggedAddonLines(cart, fallback) {
      const items = Array.isArray(cart?.items) ? cart.items : null;
      if (!items) return fallback;
      const merged = new Map();
      items.forEach((i) => {
        const p = i.properties || {};
        if (p._bundle_role !== 'addon' || !p._bundle_cat) return;
        const key = JSON.stringify([i.variant_id, p._bundle_cat, p._bundle_page, p._bundle_section]);
        const prev = merged.get(key);
        merged.set(key, { variantId: i.variant_id, quantity: (prev ? prev.quantity : 0) + i.quantity, rule: p._bundle_cat,
          ...(p._bundle_page || p._bundle_section ? { page: { path: p._bundle_page, section: p._bundle_section } } : {}) });
      });
      return merged.size ? Array.from(merged.values()) : fallback;
    }

    async applyCodes({ bundleId, cartJson, lines }) {
      const payload = {
        bundle_id: bundleId,
        machine_variant_id: this.machine.variantId,
        cart_token: normaliseToken(cartJson.token),
        company_location_id: this.config.companyLocationId || null,
        lines: lines.map((l) => ({ variant_id: l.variantId, quantity: l.quantity, rule: l.rule, ...(l.page ? { page: l.page } : {}) }))
      };

      let result = {};
      let ok = false;
      try {
        const response = await fetch(this.config.claimUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        try { result = await response.json(); } catch (e) { result = {}; }
        if (response.ok && result.code === 'no_discount') result = { ...result, success: true, codes: [] };
        ok = response.ok && result.success !== false && Array.isArray(result.codes);
        if (!ok) this.status(result.code === 'unavailable' ? this.strings.unavailable : (result.error || this.strings.applyFailed), 'error');
      } catch (e) {
        this.status(this.strings.applyFailed, 'error');
      }
      if (!ok) return null;

      rememberClaim({
        bundleId,
        codes: result.codes,
        machineVariantId: this.machine.variantId,
        cartToken: payload.cart_token,
        lines: payload.lines,
        expiresAt: result.expires_at || null,
        claimUrl: this.config.claimUrl,
        releaseUrl: this.config.releaseUrl,
        createdAt: Date.now(),
        pricingContext: JSON.stringify([String(this.config.customerId || ''), String(this.config.companyLocationId || ''), !!this.config.isB2B, 2])
      });

      const existing = Array.isArray(cartJson.discount_codes) ? cartJson.discount_codes.map((d) => d.code) : [];
      const codes = Array.from(new Set([...existing.filter((code) => !/^(BNDL-DISCOUNT-|PKG-)/.test(code)), ...result.codes]));

      try {
        const response = await fetch(theme.routes.cart_update_url, {
          ...theme.utils.fetchConfig('json'),
          body: JSON.stringify({ discount: codes.join(','), sections: this.bundledSections() })
        });
        const updated = await response.json();
        if (updated.status) { this.status(this.strings.applyFailed, 'error'); return null; }
        const applied = Array.isArray(updated.discount_codes) ? updated.discount_codes : null;
        if (applied) {
          const missing = result.codes.filter((c) => !applied.some((d) => d.code === c && d.applicable !== false));
          if (missing.length) this.status(this.strings.applyFailed, 'error');
        }
        return updated;
      } catch (e) {
        this.status(this.strings.applyFailed, 'error');
        return null;
      }
    }

    reset() {
      this.querySelectorAll('[data-bundle-builder-check]').forEach((box) => { box.checked = false; });
      this.querySelectorAll('[data-bundle-builder-qty-wrap]').forEach((w) => { w.hidden = true; });
      this.querySelectorAll('[data-bundle-builder-qty]').forEach((q) => { q.value = '1'; });
      this.render();
    }
  }

  customElements.define('commercial-bundle-builder', CommercialBundleBuilder);
})();
